import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "./observability";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary. A render-time throw anywhere in the tree (a malformed
 * card, a text-to-speech edge case, a bad slideshow frame) would otherwise unmount
 * the whole app to a blank white screen. This catches it and shows a themed recover
 * screen so the reader can reload instead of seeing nothing.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
  }

  private handleReload = (): void => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          background: "#0b0a0f",
          color: "#f4f1ea",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 800, fontSize: "20px" }}>
          <span style={{ width: "11px", height: "11px", borderRadius: "3px", background: "#eec25b" }} />
          InSnip
        </div>
        <p style={{ margin: 0, fontSize: "17px", maxWidth: "360px" }}>
          Something went wrong loading your feed.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: "13px 22px",
            borderRadius: "12px",
            border: "none",
            cursor: "pointer",
            background: "linear-gradient(135deg,#f7db8e,#eec25b)",
            color: "#1b1304",
            fontWeight: 800,
            fontSize: "15px",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
